// Name-swap planners for DML column targets — each replaces ONE identifier and
// leaves everything around it byte-identical:
//
// - modify-assignment-target: the column a `col = value` assignment writes to,
//   in the top-level UPDATE SET list, a MERGE branch's SET list (`mergeBranch`)
//   or an INSERT conflict tail (`tail`). The value is untouched.
// - modify-insert-column: the n-th name of an INSERT's explicit column list.
//   The VALUES cells stay put, so the value that sat under the old column now
//   goes into the new one — the swap the visual editor offers.

import type { PlannedByteEdit, StructuralEdit, UpdateAssignment } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { resolveAssignments } from './applyStructuralEdit.conflict';
import { pickBranch } from './applyStructuralEdit.merge';
import { replaceEdit } from './applyStructuralEdit.shared';

type AssignmentTargetEdit = Extract<StructuralEdit, { op: 'modify-assignment-target' }>;
type InsertColumnEdit = Extract<StructuralEdit, { op: 'modify-insert-column' }>;

function assignmentList(ctx: EditCtx, edit: AssignmentTargetEdit): readonly UpdateAssignment[] | null {
  if (edit.mergeBranch) {
    const branch = pickBranch(ctx, edit.mergeBranch.branchKind, edit.mergeBranch.branchIndex);
    return branch?.updateAssignments ?? null;
  }
  if (edit.tail) return resolveAssignments(ctx, edit.tail);
  return ctx.structure.updateAssignments ?? null;
}

export function planModifyAssignmentTarget(
  ctx: EditCtx,
  edit: AssignmentTargetEdit,
): readonly PlannedByteEdit[] {
  const text = edit.newTarget.trim();
  const list = assignmentList(ctx, edit);
  if (!text || !list || edit.index < 0 || edit.index >= list.length) return [];
  const { range } = list[edit.index].target;
  return [replaceEdit(ctx, range.startOffset, range.endOffset, text, 'modify-assignment-target')];
}

export function planModifyInsertColumn(
  ctx: EditCtx,
  edit: InsertColumnEdit,
): readonly PlannedByteEdit[] {
  const text = edit.columnText.trim();
  const cols = ctx.structure.insertColumns ?? [];
  if (!text || edit.index < 0 || edit.index >= cols.length) return [];
  const { range } = cols[edit.index];
  return [replaceEdit(ctx, range.startOffset, range.endOffset, text, 'modify-insert-column')];
}
